import React from 'react'
import PropTypes from 'prop-types'
import { Picker } from '../Picker/Picker'
import deprecated from '../../utils/deprecated'

/**
 * Bronson DatePicker component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-datepicker.html
 *
 * @param {string} [value] - Date value
 * @param {string} [name] - Name attribute of input element
 * @param {string} [dateFormat='Y-m-d'] - Date format used by flatpickr, @see https://flatpickr.js.org/options/
 * @param {string} [placeholder] - placeholder shown when no date is selected
 * @param {string} [locale] - Sets locale of date picker labels, one of ['pl', 'de', 'it', 'cs', 'es', 'fr', 'nl', 'pt', 'ja', 'sk'] (ISO-639-1 code)
 * @param {(string | Date)} [minDate] - Minimum date that can be selected (inclusive), @see https://flatpickr.js.org/options/
 * @param {(string | Date)} [maxDate] - Maximum date that can be selected (inclusive), @see https://flatpickr.js.org/options/
 * @param {('single' | 'multiple' | 'range')} [mode='single'] - Flatpickr mode, 'single', 'multiple' or 'range' values are selectable
 * @param {boolean} [maskedInput] - masked input (react-imask) is used instead of HTML default input, only available for mode 'single', default: true
 * @param {boolean} [monthSelectorDropdown=false] - Month selector variant
 * @param {Array} [disableDays] - Convenience prop to disable certain days, e.g. ["2020-01-30", "2020-02-21", "2020-03-08", new Date(2020, 4, 9) ], [{ from: "2025-04-01", to: "2025-05-01" }], @see https://flatpickr.js.org/examples/#disabling-dates
 * @param {boolean} [noBackground=true] - By default the addon has a background. To remove the background and only show the addon text and/or icon.
 * @param {boolean} [disabled=false] - Disabled state
 * @param {function} [onBlur] - onBlur handler
 * @param {function} [onChange] - onChange handler
 * @param {boolean} [error=false] - Error state
 * @param {object} [flatpickrOptions] - Additional flatpickr options
 * @param {object} [flatpickrProps] - Additional props passed to Flatpickr React component
 * @param {string} [className=''] - Additional CSS classes on Flatpickr element (div with class c-input)
 * @param {string} [datePickerClassName=''] - Additional CSS classes on input element of Flatpickr (input with class c-input__input)
 * @param {string} [testId] - data-testid attribute on table
 * @param {boolean} [showMask] - deprecated, use 'maskedInput'
 * @param {boolean} [guide] - deprecated, use 'maskedInput'
 * @return {JSX.Element} - The DatePicker element.
 */
export function DatePicker({
  className,
  dateFormat = 'Y-m-d',
  datePickerClassName,
  disabled = false,
  disableDays,
  error = false,
  flatpickrOptions,
  flatpickrProps,
  locale,
  maskedInput = true,
  maxDate,
  minDate,
  mode = 'single',
  monthSelectorDropdown = false,
  name,
  noBackground = true,
  onBlur,
  onChange,
  placeholder,
  testId,
  value,
  ...otherProps
}) {
  return (
    <Picker
      className={className}
      componentClassName="c-datepicker js-datepicker"
      dateFormat={dateFormat}
      disabled={disabled}
      disableDays={disableDays}
      error={error}
      flatpickrOptions={flatpickrOptions}
      flatpickrProps={flatpickrProps}
      locale={locale}
      maskedInput={maskedInput}
      maxDate={maxDate}
      minDate={minDate}
      mode={mode}
      monthSelectorDropdown={monthSelectorDropdown}
      name={name}
      noBackground={noBackground}
      onBlur={onBlur}
      onChange={onChange}
      pickerClassName={datePickerClassName}
      placeholder={placeholder}
      testId={testId}
      value={value}
      {...otherProps}
    />
  )
}

DatePicker.propTypes = {
  className: PropTypes.string,
  dateFormat: PropTypes.string,
  datePickerClassName: PropTypes.string,
  disabled: PropTypes.bool,
  disableDays: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date), PropTypes.object])), // convenience prop to disable certain days, e.g. ["2020-01-30", "2020-02-21", "2020-03-08", new Date(2020, 4, 9) ], [{ from: "2025-04-01", to: "2025-05-01" }]
  error: PropTypes.bool,
  flatpickrOptions: PropTypes.oneOfType([PropTypes.object]),
  flatpickrProps: PropTypes.oneOfType([PropTypes.object]),
  locale: PropTypes.oneOf(['pl', 'de', 'it', 'cs', 'es', 'fr', 'nl', 'pt', 'ja', 'sk']), // ISO-639-1 code
  maskedInput: PropTypes.bool,
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  mode: PropTypes.string,
  monthSelectorDropdown: PropTypes.bool,
  name: PropTypes.string,
  noBackground: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  testId: PropTypes.string,
  value: PropTypes.string,
  /**
   * Deprecated props
   */
  showMask: deprecated(
    PropTypes.bool,
    'Please use "maskedInput" instead. Prop will be removed in Bronson-React v6.0.0.'
  ),
  guide: deprecated(PropTypes.bool, 'Please use "maskedInput" instead. Prop will be removed in Bronson-React v6.0.0.'),
}
